import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  Visibility,
  Brush,
  Send,
  Alarm,
  CallMadeOutlined,
  DraftsOutlined,
  AccessTimeOutlined,
  HighlightOffOutlined,
  CheckCircleOutline,
} from '@material-ui/icons';
import { CircularProgress } from '@material-ui/core';
import { fullloader, toast } from '../../../redux/action';
import {
  selectedOrganizationSelector,
  userDetailSelector,
} from '../../../redux/selector';
import {
  saveNewDraftService,
  mergeABdraftsService,
} from '../../../redux/services';
import {
  Container,
  Wrapper,
  Title,
  SubTitle,
  Button,
  Wrap,
  Center,
  Row,
  Highlight,
  SpaceBetween,
  Btn,
  FlexRow,
  FlexColumn,
  FixedColumn,
  Info,
  Bold,
  RowSpaceBetween,
} from './MessageDetails.styled';
import ClickActivity from './ClickActivity/ClickActivity';
import OpenActivity from './OpenActivity/OpenActivity';
import DeliveryActivity from './DeliveryActivity/DeliveryActivity';
import SmartResendDrawer from './SmartResendDrawer';
import MessageDetailsDrawer from './MessageDetailsDrawer';
import { cancelSmartResend } from './SmartResendDrawer/api';

const messageStatusIcon = {
  Sent: (
    <CheckCircleOutline
      style={{
        fontSize: '20px',
        color: '#5EAB00',
        paddingLeft: '4px',
        paddingBottom: '3px',
      }}
    />
  ),
  Scheduled: (
    <AccessTimeOutlined
      style={{
        fontSize: '20px',
        color: '#F7C604',
        paddingLeft: '4px',
        paddingBottom: '3px',
      }}
    />
  ),
  Cancelled: (
    <HighlightOffOutlined
      style={{
        fontSize: '20px',
        color: '#E63946',
        paddingLeft: '4px',
        paddingBottom: '3px',
      }}
    />
  ),
};

const MAX_SUBJECT_LENGTH = 200;

function getDisplaySubject(subject) {
  const text = subject || 'N/A';
  if (text.length <= MAX_SUBJECT_LENGTH) return text;
  return `${text.slice(0, MAX_SUBJECT_LENGTH)}...`;
}

const MessageDetails = ({
  message,
  isSuperUser,
  topics,
  groups,
  isLoading,
  isClickActivityVisible,
  setClickActivityVisibility,
  setSelectedMessage,
  addStatusesToMessage,
  isOpenActivityVisible,
  setOpenActivityVisibility,
  isDeliveryActivityVisible,
  setDeliveryActivityVisibility,
  deliveryChannel,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const organization = useSelector(selectedOrganizationSelector);
  const user = useSelector(userDetailSelector);
  const [isSmartResendDrawerVisible, setSmartResendDrawerVisibility] =
    useState(false);
  const [isSmartResendAvailable, setSmartResendAvailability] = useState(false);
  const [hasAlreadyUsedSmartResend, setAlreadyUsedSmartResend] =
    useState(false);
  const [smartResendScheduledDate, setSmartResendScheduledDate] =
    useState(null);
  const [isSmartResendCancelled, setSmartResendCancelled] = useState(null);
  const [smartResendScheduledDateAndTime, setSmartResendScheduledDateAndTime] =
    useState(null);
  const [isCancellingSmartResend, setCancellingSmartResend] = useState(false);
  const [isMessageDetailsDrawerVisible, setMessageDetailsDrawerVisibility] =
    useState(false);

  const isABmessage = message && message.AB && message.AB.linkedMessageId;

  useEffect(() => {
    // if message was sent out within a week, there is still a chance to resend email to non-openers
    if (message?.sendTime) {
      const sendTimeInMilliseconds = message.sendTime * 1000;
      const sendTime = moment(sendTimeInMilliseconds);
      const tenDaysAgo = moment().subtract(10, 'days');
      setSmartResendAvailability(sendTime.isAfter(tenDaysAgo));
    }

    // if resend was already sent then there is no option to resend it again
    if (
      message?.smartResendScheduleTime &&
      moment(message.smartResendScheduleTime * 1000).isBefore(moment())
    ) {
      setAlreadyUsedSmartResend(true);
    } else {
      setAlreadyUsedSmartResend(false);
    }

    if (message?.smartResendScheduleTime) {
      const date = moment(message.smartResendScheduleTime * 1000).format(
        'MM/DD/YY'
      );
      const dateAndTime = moment(message.smartResendScheduleTime * 1000).format(
        'MM/DD/YY [at] hh:mm a'
      );
      setSmartResendScheduledDate(date);
      setSmartResendScheduledDateAndTime(dateAndTime);
    } else {
      setSmartResendScheduledDate(null);
      setSmartResendScheduledDateAndTime(null);
    }

    setSmartResendCancelled(
      message?.cancelled && message?.smartResendScheduleTime
    );
  }, [message]);

  const convertMessageToDraft = message => {
    const draft = {
      name: message.draftName,
      subject: message.messageSubject,
      sender: message.from,
      senderName: message.sender,
      replyTo: message.replyTo,
      preheader: message.preheader,
      html: message.messageBody,
      design: message.messageDesign,
      saveTime: moment().unix(),
      userId: user.id,
      organizationId: organization.id,
      topicIds: message.topicIds || [],
      selectedGroupIds: message.selectedGroupIds || [],
      requiredGroupIds: message.requiredGroupIds || [],
      excludedGroupIds: message.excludedGroupIds || [],
      includedMemberIds: message.includedMemberIds || [],
      excludedMemberIds: message.excludedMemberIds || [],
      filterData: { ...(message.filterData || {}) },
      status: 'Active',
    };

    return draft;
  };

  const editMessage = async () => {
    dispatch(fullloader(true, 'Loading edited message...'));
    const draftData = convertMessageToDraft(message);
    const draft = await saveNewDraftService(draftData);
    if (isABmessage) {
      const draftBdata = convertMessageToDraft({
        ...message.versionB,
        draftName: draft.name,
      });

      await mergeABdraftsService(draft._id, draftBdata);
    }
    dispatch(fullloader(false));
    history.push(`/dashboard/sendmessage?draft=${draft._id}`);
  };

  const smartResend = () => {
    setSmartResendDrawerVisibility(true);
  };

  const viewMessage = () => {
    window.open(
      `${process.env.REACT_APP_webUrl}/message?id=${message.id}`,
      '_blank'
    );

    if (isABmessage) {
      window.open(
        `${process.env.REACT_APP_webUrl}/message?id=${message.versionB.id}`,
        '_blank'
      );
    }
  };

  const getContent = () => {
    if (isLoading) {
      return (
        <Center>
          <CircularProgress
            style={{ margin: 'auto', alignItems: 'center', color: '#007ADE' }}
          />
        </Center>
      );
    }
  };

  const handleDownload = (blob, fileName) => {
    const href = URL.createObjectURL(blob);

    // create "a" HTLM element with href to file
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL (helps prevent memory leaks)
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const downloadMessageFile = (messageData, fileName) => {
    const messageJson = JSON.stringify(messageData.messageDesign, null, 2);
    const jsonBlob = new Blob([messageJson], { type: 'application/json' });

    const jsonFile = fileName + '.json';
    handleDownload(jsonBlob, jsonFile);

    const htmlBlob = new Blob([messageData.messageBody], {
      type: 'text/plain',
    });
    const htmlFile = fileName + ' (html)' + '.txt';
    handleDownload(htmlBlob, htmlFile);
  };

  const exportMessageDesign = () => {
    const fileName = message.messageSubject;
    downloadMessageFile(message, fileName);

    //If there is a B version, also download the B version
    if (message.versionB?._id) {
      const versionBFileName = message.versionB.messageSubject;
      downloadMessageFile(message.versionB, versionBFileName);
    }
  };

  const cancelScheduledSmartResend = async () => {
    try {
      setCancellingSmartResend(true);
      const { updatedMsg } = await cancelSmartResend(message._id);
      setSelectedMessage(addStatusesToMessage(updatedMsg));
      setCancellingSmartResend(false);
      dispatch(toast('success', 'Successfully cancelled a smart resend'));
    } catch (e) {
      dispatch(toast('error', 'Error cancelling a smart resend'));
    }
  };

  // if there is no message selected do not show anything for message details
  if (!message) {
    return null;
  }

  return (
    <Container>
      {isLoading ? (
        getContent()
      ) : (
        <Wrapper>
          <FlexRow>
            <FlexColumn>
              <Row>
                <Title>{message.draftName || 'N/A'}</Title>{' '}
                {isABmessage && <Highlight>(A/B)</Highlight>}
              </Row>
              <SubTitle>{getDisplaySubject(message.messageSubject)}</SubTitle>
            </FlexColumn>
            <FixedColumn>
              <Info>
                Send Date:
                <Bold>
                  {moment.unix(message.sendTime).format('MMM Do YYYY')}
                </Bold>
              </Info>
              <Info>
                Status: {messageStatusIcon[message.status]}
                <Bold>{message.status}</Bold>
              </Info>
            </FixedColumn>
          </FlexRow>
          <SpaceBetween>
            <Wrap style={{ paddingBottom: 5 }}>
              <Button onClick={viewMessage}>
                <Visibility fontSize="inherit" /> View
              </Button>
              <Button onClick={editMessage}>
                <Brush fontSize="inherit" /> Duplicate to Draft
              </Button>
              <Button
                disabled={
                  !!smartResendScheduledDate ||
                  !isSmartResendAvailable ||
                  hasAlreadyUsedSmartResend
                }
                onClick={smartResend}
              >
                <Send fontSize="inherit" />
                {isSmartResendCancelled
                  ? 'Cancelled Resend'
                  : smartResendScheduledDate
                  ? `Resent on ${smartResendScheduledDate}`
                  : 'Schedule Resend to Non-Openers'}
              </Button>
              {isSuperUser && (
                <Button onClick={exportMessageDesign}>
                  <CallMadeOutlined fontSize="inherit" />
                  Export HTML/JSON
                </Button>
              )}
            </Wrap>
          </SpaceBetween>
          <RowSpaceBetween>
            {hasAlreadyUsedSmartResend ||
              (isSmartResendAvailable &&
                smartResendScheduledDateAndTime &&
                !isSmartResendCancelled && (
                  <Row>
                    <Alarm
                      style={{
                        fontSize: '20px',
                        color: '#E9505C',
                        paddingLeft: '4px',
                      }}
                    />
                    <SubTitle fontSize="13px">
                      Resend to non-openers scheduled:{' '}
                      {smartResendScheduledDateAndTime}
                    </SubTitle>
                    <Btn onClick={cancelScheduledSmartResend}>
                      {isCancellingSmartResend ? (
                        <>
                          Cancelling{' '}
                          <CircularProgress size={16} color="inherit" />
                        </>
                      ) : (
                        'Cancel'
                      )}
                    </Btn>
                  </Row>
                ))}
            {isSmartResendCancelled && (
              <Row>
                <SubTitle fontSize="13px">
                  Resend to non-openers cancelled
                </SubTitle>
              </Row>
            )}
            <Btn
              onClick={() => {
                setMessageDetailsDrawerVisibility(true);
              }}
            >
              <DraftsOutlined fontSize="inherit" />
              View more message details
            </Btn>
          </RowSpaceBetween>
        </Wrapper>
      )}
      <ClickActivity
        isClickActivityVisible={isClickActivityVisible}
        setClickActivityVisibility={setClickActivityVisibility}
        initialMessage={message}
      />
      <OpenActivity
        initialMessage={message}
        orgId={organization.id}
        isOpenActivityDrawerVisible={isOpenActivityVisible}
        setOpenActivityVisibility={setOpenActivityVisibility}
      />
      <DeliveryActivity
        initialMessage={message}
        orgId={organization.id}
        isDeliveryActivityDrawerVisible={isDeliveryActivityVisible}
        setDeliveryActivityVisibility={setDeliveryActivityVisibility}
        channel={deliveryChannel}
      />
      {isSmartResendDrawerVisible && (
        <SmartResendDrawer
          isDrawerVisible={isSmartResendDrawerVisible}
          setDrawerdVisibility={setSmartResendDrawerVisibility}
          message={message}
          setSelectedMessage={setSelectedMessage}
          addStatusesToMessage={addStatusesToMessage}
        />
      )}
      {isMessageDetailsDrawerVisible && (
        <MessageDetailsDrawer
          message={message}
          isSuperUser={isSuperUser}
          topics={topics}
          groups={groups}
          isLoading={isLoading}
          isDrawerVisible={isMessageDetailsDrawerVisible}
          closeDrawer={() => setMessageDetailsDrawerVisibility(false)}
        />
      )}
    </Container>
  );
};

export default MessageDetails;
